import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Menu, ShoppingBag, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCart } from '@/components/generals/cart-provider'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'
import Wordmark from '@/components/generals/wordmark'
import { ATELIER, NAV_LINKS } from '@/constants/site'

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const { count, ready } = useCart()
    const { stop, start } = useSmoothScroll()
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const menuRef = useRef<HTMLDivElement | null>(null)
    const toggleRef = useRef<HTMLButtonElement | null>(null)

    const isActive = (href: string): boolean => router.pathname === href || router.pathname.startsWith(`${href}/`)

    const closeMenu = useCallback((): void => {
        setOpen(false)
        start()
    }, [start])

    const openMenu = (): void => {
        setOpen(true)
        stop()
    }

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 12)
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        router.events.on('routeChangeStart', closeMenu)
        return () => router.events.off('routeChangeStart', closeMenu)
    }, [router.events, closeMenu])

    useEffect(() => {
        if (!open) return

        menuRef.current?.querySelector<HTMLElement>('a')?.focus()

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                closeMenu()
                toggleRef.current?.focus()
            }
        }

        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, closeMenu])

    const cartLabel = ready && count > 0 ? `Cart, ${count} ${count === 1 ? 'item' : 'items'}` : 'Cart'

    return (
        <header
            className={cn(
                'sticky top-0 z-50 bg-paper/95 transition-[box-shadow,border-color] duration-500',
                'border-b',
                scrolled ? 'border-bark/12 shadow-[0_10px_30px_-24px_rgba(58,42,31,0.45)]' : 'border-transparent'
            )}
        >
            <a
                href='#main'
                className='sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[70] focus:bg-bark focus:px-4 focus:py-2 focus:text-sm focus:text-paper'
            >
                Skip to content
            </a>

            <div className='container-page flex h-[72px] items-center justify-between gap-6 md:h-20'>
                <Link href='/' aria-label="Qog'oz Atelier, home" className='shrink-0 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bark'>
                    <Wordmark />
                </Link>

                <nav aria-label='Main' className='hidden md:block'>
                    <ul className='flex items-center gap-9'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    aria-current={isActive(link.href) ? 'page' : undefined}
                                    className={cn(
                                        'relative py-2 text-[0.95rem] text-bark-soft transition-colors hover:text-bark',
                                        'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bark',
                                        isActive(link.href) && 'text-bark'
                                    )}
                                >
                                    {link.label}
                                    {isActive(link.href) && (
                                        <span aria-hidden='true' className='absolute -bottom-0.5 left-1/2 -ml-[3px] size-1.5 rounded-full bg-pomegranate' />
                                    )}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-1 md:gap-2'>
                    <Link
                        href='/cart'
                        aria-label={cartLabel}
                        className='relative inline-flex size-11 items-center justify-center rounded-full text-bark transition-colors hover:bg-bark/6 focus-visible:outline-2 focus-visible:outline-bark'
                    >
                        <ShoppingBag className='size-5' strokeWidth={1.6} />
                        {ready && count > 0 && (
                            <span className='absolute right-1 top-1 flex min-w-[1.15rem] items-center justify-center rounded-full bg-pomegranate px-1 text-[0.65rem] font-semibold leading-[1.15rem] text-paper'>
                                {count}
                            </span>
                        )}
                    </Link>

                    <button
                        ref={toggleRef}
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        onClick={open ? closeMenu : openMenu}
                        className='inline-flex size-11 items-center justify-center rounded-full text-bark transition-colors hover:bg-bark/6 focus-visible:outline-2 focus-visible:outline-bark md:hidden'
                    >
                        {open ? <X className='size-5' strokeWidth={1.6} /> : <Menu className='size-5' strokeWidth={1.6} />}
                    </button>
                </div>
            </div>

            <div
                id='mobile-menu'
                ref={menuRef}
                hidden={!open}
                data-lenis-prevent
                className='fixed inset-x-0 bottom-0 top-[72px] z-40 overflow-y-auto bg-paper md:hidden'
            >
                <nav aria-label='Mobile' className='container-page flex min-h-full flex-col justify-between pb-10 pt-8'>
                    <ul className='flex flex-col'>
                        {NAV_LINKS.map((link, index) => (
                            <li key={link.href} className='border-b border-bark/10'>
                                <Link
                                    href={link.href}
                                    aria-current={isActive(link.href) ? 'page' : undefined}
                                    className='flex items-baseline justify-between py-5 focus-visible:outline-2 focus-visible:outline-bark'
                                >
                                    <span className={cn('font-heading text-[2.4rem] leading-none text-bark', isActive(link.href) && 'text-pomegranate')}>
                                        {link.label}
                                    </span>
                                    <span className='text-sm tabular-nums text-bark-soft'>
                                        {index + 1}
                                    </span>
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link href='/cart' className='flex items-center justify-between py-5 text-bark-soft focus-visible:outline-2 focus-visible:outline-bark'>
                                <span className='text-base'>Your cart</span>
                                <span className='text-sm'>{ready ? count : 0} {count === 1 ? 'item' : 'items'}</span>
                            </Link>
                        </li>
                    </ul>

                    <div className='mt-12 space-y-1 text-sm text-bark-soft'>
                        <p>{ATELIER.region}</p>
                        <p>{ATELIER.hours}</p>
                    </div>
                </nav>
            </div>
        </header>
    )
}

export default SiteHeader
